import { MinimizedCentraProduct } from "@/types/centra/checkout-api/products/post-products"
import React from "react"
import placeholder from "@assets/images/placeholder.png"
import { transformBenefitsList } from "@/app/(site)/_utils/transformData/transformBenfitList"

type CurrentProduct = {
    [key: string]: any
}

/**
 * A helper function to manage state and derive additional product data for presentation in a UI component.
 * @param product - The product object derived from the CheckoutAPIProduct type.
 * @param override - An optional parameter for overriding specific product attributes.
 * @returns An object containing several pieces of derived product data, along with the current product state and a function to update it.
 */
export const useProductDisplayInfo = (
    products: MinimizedCentraProduct[],
    override?: any
) => {
    const [currentProduct, setCurrentVariant] = React.useState<CurrentProduct>({
        image: products?.[0]?.mediaObjects?.small?.[0] ?? "",
        name: products?.[0]?.name ?? "",
        price: products?.[0]?.price ?? "",
        priceBeforeDiscount: products?.[0]?.priceBeforeDiscount ?? "",
        available: products?.[0]?.available ?? false,
        uri: products?.[0]?.uri ?? "",
        showAsOnSale: products?.[0]?.showAsOnSale ?? "",
        color_swatch: products?.[0]?.color_swatch ?? "",
        showAsNew: products?.[0]?.showAsNew ?? "",
        priceAsNumber: products?.[0]?.priceAsNumber ?? "",
        imageSources: {
            small: products?.[0]?.mediaObjects.small ?? [],
            standard: products?.[0]?.mediaObjects.standard ?? [],
            big: products?.[0]?.mediaObjects.big ?? []
        },
        product_benefits_list: products?.[0]?.product_benefits_list,
        badges: products?.[0]?.badge
    })

    React.useEffect(() => {
        setCurrentVariant({
            image: products?.[0]?.mediaObjects.small?.[0] ?? "",
            name: products?.[0]?.name ?? "",
            price: products?.[0]?.price ?? "",
            priceBeforeDiscount: products?.[0]?.priceBeforeDiscount ?? "",
            available: products?.[0]?.available ?? false,
            uri: products?.[0]?.uri ?? "",
            showAsOnSale: products?.[0]?.showAsOnSale ?? "",
            color_swatch: products?.[0]?.color_swatch ?? "",
            showAsNew: products?.[0]?.showAsNew ?? "",
            priceAsNumber: products?.[0]?.priceAsNumber ?? "",
            imageSources: {
                small: products?.[0]?.mediaObjects.small ?? [],
                standard: products?.[0]?.mediaObjects.standard ?? [],
                big: products?.[0]?.mediaObjects.big ?? []
            },
            product_benefits_list: products?.[0]?.product_benefits_list,
            badges: products?.[0]?.badge
        })
    }, [products])

    const uri = "/product/" + (products?.[0]?.uri ?? "")
    const badges = products?.[0]?.badge
    const colorVariantsAmount = products?.length ?? 0

    const {
        name,
        available,
        priceBeforeDiscount,
        price,
        showAsOnSale,
        showAsNew,
        product_benefits_list,
        imageSources
    } = currentProduct

    const imageStandard = products?.[0]?.mediaObjects.small?.[0]

    const benefits = transformBenefitsList(product_benefits_list).splice(0, 3)

    const colorSwatches = [
        ...(Array.isArray(products)
            ? products.map(product => ({
                  product: product as MinimizedCentraProduct,
                  name: product?.name,
                  color: product?.color_swatch?.hex,
                  sortOrder: Number(product?.variant_sort_order_sort_order),
                  uri: product?.uri,
                  priceBeforeDiscount: product?.priceBeforeDiscount,
                  lens: product?.lens,
                  mediaObjects: product?.mediaObjects,
                  price: product?.price,
                  available: product?.available,
                  showAsNew: product?.showAsNew,
                  showAsOnSale: product?.showAsOnSale,
                  image: product?.lens?.image,
                  badges: product?.badge
              }))
            : [])
    ].filter(swatch => swatch.color)

    let sortedColor = colorSwatches.sort((a, b) => {
        const orderA = a.sortOrder || Infinity
        const orderB = b.sortOrder || Infinity
        return orderA - orderB || (a?.uri || "").localeCompare(b?.uri || "")
    })

    const hoverImage = products[0]?.mediaObjects.small?.[0] ?? placeholder
    const productImage = override?.image?.url || imageStandard || placeholder
    const productImageAlt = override?.image?.alt || name
    const extraColorsCount = sortedColor.length > 6 ? sortedColor.length - 6 : 0

    sortedColor = sortedColor.slice(0, 6)

    const anyVariantisAvailable = isProductAvailability(products)

    return {
        currentProduct,
        setCurrentVariant,
        uri,
        badges,
        colorVariantsAmount,
        benefits,
        sortedColor,
        hoverImage,
        available,
        anyVariantisAvailable,
        imageStandard,
        imageSources,
        showAsNew,
        extraColorsCount,
        name,
        priceBeforeDiscount,
        price,
        showAsOnSale,
        productImage,
        productImageAlt
    }
}

export function isProductAvailability(products: MinimizedCentraProduct[]): boolean {
    // Check if products is an array and has at least one item
    if (!Array.isArray(products) || products.length < 1) {
        return false
    }

    // Check if any item in the products array is available
    const isAvailable = products.some(item => item.available)
    return isAvailable
}
